import React, { useMemo } from "react";
import DOMPurify from "dompurify";

/**
 * Renderer sicuro per HTML prodotto dal RichTextEditor (Quill).
 * Usa DOMPurify per bloccare XSS (script, on*, javascript: url, iframe, ecc.).
 *
 * Retro-compat: se il contenuto non contiene tag HTML (post vecchi in plain
 * text), rendo con whitespace-pre-line cosi' i vecchi post non perdono
 * la formattazione delle newline.
 *
 * Props:
 *  - html: string (contenuto sanitizzato e visualizzato)
 *  - className: string opzionale per lo styling wrapper
 */
const looksLikeHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "b", "em", "i", "u", "s", "strike", "del",
    "h2", "h3", "h4",
    "ul", "ol", "li",
    "blockquote",
    "a",
    "hr",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ALLOW_DATA_ATTR: false,
};

// Force all links to open in nuova tab con rel di sicurezza
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const HtmlContent = ({ html, className = "" }) => {
  const isHtml = looksLikeHtml(html);

  const sanitized = useMemo(() => {
    if (!html || !isHtml) return "";
    // Quill spesso inserisce &nbsp; tra le parole (soprattutto quando
    // l'utente preme piu' spazi o incolla da Word). Il browser tratta le
    // parole separate da nbsp come una singola "super-parola" e le spezza
    // a meta' quando non ci stanno nella colonna. Convertiamo entrambe le
    // forme (entita' HTML e char U+00A0) in spazi normali.
    const preprocessed = html
      .replace(/&nbsp;/gi, " ")
      .replace(/ /g, " ");
    return DOMPurify.sanitize(preprocessed, PURIFY_CONFIG);
  }, [html, isHtml]);

  if (!html) return null;

  if (!isHtml) {
    // Plain text legacy: preserva newline
    return (
      <div className={`whitespace-pre-line ${className}`}>{html}</div>
    );
  }

  return (
    <div
      className={`rich-content ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default HtmlContent;
